import type { ReactNode } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { IconShare2 } from '@tabler/icons-react';
import { isRecentlyListed } from 'components/marketplace/helpers/certaintyPanel';
import { CardLink } from 'components/marketplace/PropertyCard';
import { FavoriteHeartIcon } from 'components/marketplace/shared/FavoriteHeartIcon';
import { formatCurrencyDollars } from 'helpers/formatters';

import type { ListingCardData } from '../types';
import { PdpStatusBadge } from './PdpStatusBadge';

export const PdpSimilarListingCard = ({
  listing,
  href,
  media,
  isLiked,
  isFavoriteLoading,
  onToggleFavorite,
  onShare,
}: {
  listing: ListingCardData;
  href: string;
  media: ReactNode;
  isLiked: boolean;
  isFavoriteLoading: boolean;
  onToggleFavorite: () => void;
  onShare: () => void;
}) => {
  const title = listing.street || listing.fullAddress || 'View home';
  const facts = [
    listing.beds == null ? null : `${listing.beds} bed`,
    listing.baths == null ? null : `${listing.baths} bath`,
    listing.livingSquareFootage == null
      ? null
      : `${listing.livingSquareFootage.toLocaleString('en-US')} sqft`,
  ].filter(Boolean);
  let badge: ReactNode = null;
  if (listing.listingState === 'IN_CONTRACT')
    badge = <PdpStatusBadge label="Pending sale" tone="contract" />;
  else if (listing.listingState === 'AVAILABLE_SOON')
    badge = <PdpStatusBadge label="Coming soon" tone="neutral" />;
  else if (listing.listingState === 'RECENTLY_SOLD')
    badge = <PdpStatusBadge label="Sold" tone="neutral" />;
  else if (listing.displayBadge === 'TEMPORARILY_UNAVAILABLE')
    badge = <PdpStatusBadge label="Temporarily unavailable" tone="neutral" />;
  else if (
    listing.listingState === 'ON_THE_MARKET' &&
    isRecentlyListed(listing.daysOnMarket ?? null)
  )
    badge = <PdpStatusBadge label="Newly listed" tone="new" />;
  return (
    <Col className="min-w-0 gap-4">
      <Box className="relative aspect-[303/226] w-full overflow-hidden rounded-[20px] bg-od-bg-tertiary">
        {media}
        {badge && <Box className="pointer-events-none absolute top-3 left-3 z-1">{badge}</Box>}
        <Row className="absolute top-3 right-3 z-2 gap-2">
          <Button
            variant="secondary"
            size="sm"
            analyticsName="pdp-similar-share"
            aria-label="Share home"
            onClick={onShare}
            className="size-7 rounded-full border-0 bg-od-bg-secondary/80 p-0 shadow-none backdrop-blur-[25px]"
          >
            <IconShare2 size={16} aria-hidden />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            analyticsName="pdp-similar-favorite"
            aria-label={isLiked ? 'unfavorite' : 'favorite'}
            loading={isFavoriteLoading}
            onClick={onToggleFavorite}
            className="group/favorite size-7 rounded-full border-0 bg-od-bg-secondary/80 p-0 shadow-none backdrop-blur-[15px]"
          >
            <FavoriteHeartIcon filled={isLiked} interactive size={16} />
          </Button>
        </Row>
      </Box>
      <Col className="gap-1">
        <CardLink
          href={href}
          ariaLabel={`Property details: ${title}`}
          analyticsName="pdp-similar-title"
          className="min-w-0 justify-start text-left"
        >
          <Text as="span" className="line-clamp-1 text-label-md text-od-content-primary">
            {title}
          </Text>
        </CardLink>
        <Text as="p" className="min-h-5 whitespace-pre-wrap text-body-sm text-od-content-primary">
          {listing.listPrice == null
            ? 'Price coming soon'
            : formatCurrencyDollars(listing.listPrice)}
          {listing.estimatedMonthlyPayment != null && (
            <>
              {'  ·  '}
              <Text
                as="span"
                className="text-body-sm"
                title="Estimated monthly principal and interest with 20% down over 30 years. Taxes, insurance, and fees are excluded."
              >
                {formatCurrencyDollars(Math.round(listing.estimatedMonthlyPayment))} / mo
              </Text>
            </>
          )}
          {listing.estimatedAnnualInterestRatePercent != null && (
            <>
              {'  ·  '}
              <Text
                as="span"
                className="text-body-sm"
                title="Interest rate assumed for the payment estimate, not a personalized loan offer."
              >
                {listing.estimatedAnnualInterestRatePercent.toLocaleString('en-US', {
                  maximumFractionDigits: 2,
                })}
                % rate
              </Text>
            </>
          )}
        </Text>
        <Text as="p" className="min-h-5 whitespace-pre-wrap text-body-sm text-od-content-secondary">
          {facts.join('  ·  ')}
        </Text>
        {listing.canSelfTour && (
          <Box className="self-start pt-1">
            <Text
              as="span"
              className="inline-flex h-[19px] w-[113px] items-center whitespace-nowrap rounded-md bg-od-bg-accent-brand/10 px-1.5 text-body-xs text-od-content-accent-brand"
            >
              Self-tour available
            </Text>
          </Box>
        )}
      </Col>
    </Col>
  );
};
